import { cloneDeep, isEmpty, set } from 'lodash';
import {
    Activity,
    ActivityInput,
    DateRange,
    DependencyInput,
    InputMaybe,
    ReferenceInput
} from 'sr-types/lib/production/v1/graphql';
import { recalculateDuration } from '../../common/components/DateRangeInput';
import { client, personDetailsQuery } from '../../common/components/person/person';
import { FETCH_POLICY_CONFIG } from '../../common/constants/constants';
import { Listener } from '../../common/form/FormContext';
import Validator from '../../common/form/Validator';
import { failedToastMessage } from '../../common/utils/commonUtils';
import { serializeLabelsForCsv } from '../../common/utils/csvLabelUtils';
import { formatDateForCsv, toDisplay } from '../../common/utils/dateTime';
import { EMPTY_DATERANGE } from '../../lab/form/LabForms';
import {
    getDateRangeListeners,
    getDateRangeValidators,
    isStartBeforeEnd,
    recalculateDateRangeFromStartDate
} from '../../supply/booking/ScheduleCalculator';

export interface SaveProductionActivityParams {
    productionId: string;
    planId: string;
    input: ActivityFormType;
    activitySave: (productionId: string, planId: string, input: ActivityInput, showMessage?: boolean) => Promise<any>;
    linkResourceToActivity: (activityId: string, resourceId: string) => Promise<any>;
    unlinkResourceFromActivity: (activityId: string, resourceId: string) => Promise<any>;
    associateWithResource?: AssociateWithResource[];
    dependencies?: ActivityDependencies;
    showMessage?: boolean;
    activityData?: Activity;
}

export type SaveActivityResponse = {
    data?: {
        saveActivity: {
            id?: string;
            errors?: {
                field?: string;
                key?: string;
                message?: string;
            }[];
        };
    };
};
interface GetDateRangeByActivityTypeParams {
    isMilestone: boolean;
    dateRange: DateRange;
}

interface GetDateRangeByActivityType {
    dateRange: DateRange;
    durationSpecified: boolean;
}

export interface AssociateWithResource {
    id: string;
    label: string;
}

export enum ActivityOrder {
    predecessor = 'predecessor',
    successor = 'successor'
}

type ActivityDependency = {
    order: ActivityOrder.predecessor | ActivityOrder.successor;
    activityRefId: string;
    lag: number;
    lagUnit: string;
    type: string;
};

export type ActivityDependencies = {
    lastDependency: ActivityDependency;
    [key: string]: ActivityDependency;
};

export type ActivityFormType = ActivityInput & {
    associateWithResource?: AssociateWithResource[];
    dependencies?: ActivityDependencies;
};

export type ActivityEntityFormType = ActivityFormType & {
    productReferences?: ReferenceInput[];
    ownerOrganizationId?: String;
    ownerOrganizationName?: String;
    productionReference?: InputMaybe<ReferenceInput>;
    organizationReference?: InputMaybe<ReferenceInput>;
};

export const getDateRangeByActivityType = ({
    isMilestone,
    dateRange
}: GetDateRangeByActivityTypeParams): GetDateRangeByActivityType => {
    var dateObj = { ...dateRange };
    if (isMilestone) {
        if (dateRange.start) {
            dateObj = { ...dateRange, start: dateRange.start, durationSpecified: false };
        } else {
            dateObj = { ...dateRange, durationSpecified: false };
        }
    }
    return {
        durationSpecified: !isMilestone && dateRange.durationSpecified,
        dateRange: dateObj
    };
};

export const EMPTY_DEPENDENCIES: ActivityDependencies = {
    lastDependency: {
        order: ActivityOrder.predecessor,
        activityRefId: null,
        lag: 1,
        lagUnit: 'Days',
        type: 'Start to Start'
    }
};
export const EMPTY_ACTIVITY: ActivityFormType = {
    name: '',
    ...cloneDeep(EMPTY_DATERANGE),
    locations: [],
    tags: [],
    category: undefined,
    activityType: 'Task',
    durationSpecified: false,
    identity: { id: '' },
    associateWithResource: [],
    dependencies: { ...cloneDeep(EMPTY_DEPENDENCIES) }
};

export const getDependencyArrayFromState = (dependencies: ActivityDependencies, activityId: string) => {
    const activityDependencies = [];
    if (dependencies && activityId) {
        if (dependencies?.lastDependency?.activityRefId) {
            dependencies[dependencies.lastDependency.activityRefId] = { ...dependencies.lastDependency };
            delete dependencies.lastDependency;
        }
        Object.keys(dependencies).forEach((key) => {
            if (dependencies[key]?.activityRefId) {
                const { activityRefId, lag, lagUnit, type } = dependencies[key];
                if (dependencies[key]?.order === ActivityOrder.predecessor) {
                    const dependency: DependencyInput = {
                        from: { id: activityRefId },
                        to: { id: activityId },
                        lag: lag || 0,
                        lagUnit: lagUnit,
                        type: type
                    };
                    activityDependencies.push(dependency);
                }
                if (dependencies[key]?.order === ActivityOrder.successor) {
                    const dependency: DependencyInput = {
                        from: { id: activityId },
                        to: { id: activityRefId },
                        lag: lag || 0,
                        lagUnit: lagUnit,
                        type: type
                    };
                    activityDependencies.push(dependency);
                }
            }
        });
    }
    return activityDependencies;
};
export const reverseDependencyArrayToObject = (dependencyArray: DependencyInput[], id: string) => {
    const dependencyObject = {};
    if (dependencyArray && id) {
        dependencyArray.forEach((dependency) => {
            const { from, to, lag, lagUnit, type } = dependency;

            if (from && to && from.id && to.id) {
                const activityRefId = id === to.id ? from.id : to.id;
                const order = id === dependency.to.id ? ActivityOrder.predecessor : ActivityOrder.successor;

                dependencyObject[activityRefId] = {
                    order,
                    activityRefId,
                    lag,
                    lagUnit,
                    type
                };
            }
        });
    }

    return dependencyObject;
};

export const getActivityListeners = (locale): Listener[] => [
    ...getDateRangeListeners('dateRange', locale),
    {
        prop: `activityType`,
        func: (name, newValue, newState) => {
            if (newValue === 'Milestone') {
                set(newState, 'dateRange.end', '');
            } else {
                recalculateDateRangeFromStartDate('dateRange', newValue, newState);
            }
        }
    },
    {
        prop: `dateRange.durationSpecified`,
        func: (name, newValue, newState) => {
            if (newValue === true && !newState.dateRange?.start) {
                set(newState, 'dateRange.end', '');
            }
        }
    }
];

export const getStartDateValidators = () => {
    return {
        [`dateRange.start`]: [
            Validator.RULES.isValidDate,
            Validator.Custom((name, state, isModified) => {
                return isStartBeforeEnd('dateRange', state, isModified);
            }, 'Start is after end.'),
            Validator.Custom((name, state, isModified) => {
                const durationSpecified = state.dateRange.durationSpecified;
                const startDateSpecified = Boolean(state.dateRange.start);
                const endDateSpecified = Boolean(state.dateRange.end);
                return durationSpecified || startDateSpecified || !endDateSpecified;
            }, 'Start date needs to be specified')
        ]
    };
};

export const getActivityValidators = () => {
    return {
        ...getDateRangeValidators('dateRange'),
        ...getStartDateValidators(),
        name: [Validator.RULES.isRequired],
        activityType: [Validator.RULES.isRequired]
    };
};

export const getStringFromDateRange = (dateRange: DateRange, locale: string): string => {
    if (!dateRange) {
        return '';
    }
    let formattedDateRange = '';
    const { start, end, dateTimeDuration, durationSpecified } = dateRange;

    if (start && end && durationSpecified) {
        const startStr = toDisplay(start, true, locale);
        const endStr = toDisplay(end, true, locale);
        const durationStr = `${dateTimeDuration.value} ${dateTimeDuration.unit.charAt(0).toUpperCase()}`;

        formattedDateRange =
            start === end ? `${startStr} (${durationStr})` : `${startStr} - ${endStr} (${durationStr})`;
    } else if (start && end) {
        const startStr = toDisplay(start, true, locale);
        const endStr = toDisplay(end, true, locale);
        formattedDateRange = start === end ? `${startStr}` : `${startStr} - ${endStr}`;
    } else if (start) {
        formattedDateRange = `${toDisplay(start, true, locale)}`;
    } else if (!start && !durationSpecified) {
        formattedDateRange = '';
    } else if (dateTimeDuration?.value) {
        formattedDateRange = `(${dateTimeDuration.value} ${dateTimeDuration.unit.charAt(0).toUpperCase()})`;
    }

    return formattedDateRange;
};

export const getKeyActivitiesRefs = (
    id: string,
    keyActivities: string[],
    activityList: any[],
    doMark: boolean = false
) => {
    return activityList
        .filter((activity) =>
            doMark
                ? keyActivities.includes(activity.id) || id === activity.id
                : keyActivities.includes(activity.id) && id !== activity.id
        )
        .sort((a, b) => {
            if (a.dateRange?.start < b.dateRange?.start) {
                return -1;
            }
            if (a.dateRange?.start > b.dateRange?.start) {
                return 1;
            }
            return 0;
        })
        .map((item) => {
            return item.reference;
        });
};

export const getDateRangeFromActivities = (linkedActivities, resourceSchedule, locale) => {
    const startDates = [],
        endDates = [];
    linkedActivities.forEach((activity) => {
        if (activity?.dateRange?.start) {
            startDates.push(Number(activity.dateRange.start));
        }
        if (activity?.dateRange?.end) {
            endDates.push(Number(activity.dateRange.end));
        }
    });
    const minStartDate = isEmpty(startDates) ? '' : Math.min(...startDates).toString();
    const maxEndDate = isEmpty(endDates) ? '' : Math.max(...endDates).toString();

    if (!isEmpty(minStartDate) && !isEmpty(maxEndDate)) {
        const durationSpecified = resourceSchedule.dateRange.durationSpecified;
        const resourceDateRange = {
            ...resourceSchedule.dateRange,
            start: minStartDate,
            end: maxEndDate
        };
        resourceSchedule.dateRange = {
            ...recalculateDuration(resourceDateRange),
            durationSpecified: durationSpecified
        };
    }

    return getStringFromDateRange(resourceSchedule.dateRange, locale);
};

export const updateActivityTemplateValues = (activityData: ActivityInput) => {
    const { name, activityType, dateRange, durationSpecified } = activityData;

    return {
        name,
        activityType,
        dateRange,
        durationSpecified
    };
};

export const Activity_Field_Names = {
    basicSection: 'basicSection',
    name: 'name',
    activityType: 'activityType',
    scheduleSection: 'scheduleSection',
    locations: 'locations',
    category: 'category',
    associateWithResource: 'associateWithResource',
    trackOwnerStatusSection: 'trackOwnerStatusSection',
    activityOwner: 'activityOwner',
    activityStatus: 'activityStatus',
    tags: 'tags',
    activityDependency: 'activityDependency',
    addActivityButton: 'addActivityButton',
    'dateRange.durationSpecified': 'dateRange.durationSpecified',
    dateRange: 'dateRange',
    detailsSection: 'detailsSection',
    productionReference: 'productionReference',
    productReferences: 'productReferences',
    organizationReference: 'organizationReference',
    'summary.about': 'summary.about'
};

export const handleActivitySaveInput = (state: ActivityFormType, activityId: string = undefined) => {
    const { category, associateWithResource, dependencies, ...otherFields } = state;
    const activityInput: ActivityInput = {
        ...otherFields,
        activityDependencies: getDependencyArrayFromState(dependencies, activityId),
        ...getDateRangeByActivityType({
            isMilestone: otherFields?.activityType === 'Milestone',
            dateRange: otherFields?.dateRange
        })
    };

    if (category?.value) {
        activityInput.category = category;
    }
    return activityInput;
};

export const makeFieldMap = (keys: string[]) =>
    keys.reduce(
        (acc, key) => {
            const fieldName = Activity_Field_Names[key];
            if (fieldName) acc[fieldName] = true;
            return acc;
        },
        {} as Record<string, boolean>
    );

export const saveProductionActivity = async ({
    input,
    planId,
    productionId,
    activitySave,
    linkResourceToActivity,
    unlinkResourceFromActivity,
    associateWithResource,
    dependencies,
    showMessage,
    activityData
}: SaveProductionActivityParams): Promise<string> => {
    try {
        const response: SaveActivityResponse = await activitySave(productionId, planId, input, showMessage);
        const actId = response?.data?.saveActivity?.id;
        if (!actId) {
            throw new Error('Activity save failed: No activity ID returned');
        }
        const { linkedResources } = activityData || {};
        const alreadyLinkedIds = (linkedResources ?? []).map(({ identity }) => identity.id);
        const associateWithResourceId = (associateWithResource ?? []).map(({ id }) => id);
        const toUnlink = alreadyLinkedIds.filter((id) => !associateWithResourceId.includes(id));
        const toLink = associateWithResourceId.filter((id) => !alreadyLinkedIds.includes(id));

        toLink.forEach((id) => linkResourceToActivity(actId, id));
        toUnlink.forEach((id) => unlinkResourceFromActivity(actId, id));
        const updatedInput = {
            ...input,
            identity: { id: actId },
            activityDependencies: getDependencyArrayFromState(dependencies, actId)
        };
        await activitySave(productionId, planId, updatedInput, false);
        return actId;
    } catch (error) {
        failedToastMessage('production.activitySave.error');
        throw error;
    }
};

/**
 * Maps an array of `Activity` objects to an array of objects representing CSV rows and the sequence of fields should match with activityImportColDefs.
 *
 * Each CSV row contains the following fields:
 * - `ID`: The activity's unique identifier (string).
 * - `Name`: The name of the activity (string).
 * - `Type`: The type of the activity (string).
 * - `StartDate`: The formatted start date of the activity (string).
 * - `EndDate`: The formatted end date of the activity (string).
 * - `Units`: The unit of time for the activity's duration, pluralized (string).
 * - `Location`: The label of the activity's location (string).
 * - `Category`: The category value of the activity (string).
 * - `Summary`: A summary or description of the activity (string).
 * - `Owner`: The label of the activity owner (string).
 * - `Status`: The status of the activity (string).
 * - `Tags`: CSV-encoded tag labels, with commas and quotes inside labels escaped (string).
 *
 * @param data - An array of activity objects to be mapped. Defaults to an empty array.
 * @returns An array of objects representing CSV rows for each activity.
 */
export const mapActivitiesToCsvRows = async (data = [], ownerId: string) => {
    const activityOwnerEmailsMap = await fetchActivityOwnerEmails(data, ownerId);
    const csvRows = data.map((item) => ({
        ID: item.id || '',
        Name: item.name,
        Type: item.activityType ?? '',
        StartDate: formatDateForCsv(item.dateRange?.start),
        EndDate: formatDateForCsv(item.dateRange?.end),
        Units: item.dateRange?.dateTimeDuration?.unit ? `${item.dateRange?.dateTimeDuration?.unit}s` : '',
        Location: item.location?.label ?? '',
        Category: item.category?.value ?? '',
        Summary: item.summary?.about ?? '',
        Owner: (item?.activityOwner?.id && activityOwnerEmailsMap[item.activityOwner.id]) ?? '',
        Status: item.activityStatus ?? '',
        Tags: serializeLabelsForCsv(item.tags)
    }));
    return csvRows;
};

export const fetchActivityOwnerEmails = async (activities = [], ownerId: string) => {
    const ownerIds = [...new Set(activities.map((a) => a?.activityOwner?.id).filter(Boolean))];

    if (ownerIds.length === 0) {
        return {};
    }

    const results = await Promise.allSettled(
        ownerIds.map((id) =>
            client.query({
                query: personDetailsQuery,
                variables: { id },
                context: {
                    headers: {
                        ownerId
                    }
                },
                fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy
            })
        )
    );
    const activityOwnerEmailsMap = {};
    results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
            const personData = result.value.data.person;

            if (personData?.identity?.id) {
                const firstName = personData.name?.firstName ?? '';
                const lastName = personData.name?.lastName ?? '';
                const email = personData.contactInfo?.email?.address ?? '';
                const ownerData = `${firstName},${lastName},${email}`;

                activityOwnerEmailsMap[personData.identity.id] = ownerData;
            }
        } else {
            console.warn(`Failed to fetch owner details for ID ${ownerIds[index]}:`, result.reason);
        }
    });

    return activityOwnerEmailsMap;
};
